import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { TextB, TextItalic, TextUnderline, PaintBucket } from "@phosphor-icons/react";

/**
 * Compact single-line rich-text field for header/footer cells.
 * Supports bold, italic, underline, color + token insertion.
 * Intentionally NO headings/lists — these are single-line footer-style fields.
 */
export default function MiniRichField({ value = "", onChange, placeholder = "", tokens = [], testId = "mini-rich" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, bulletList: false, orderedList: false, listItem: false, blockquote: false, codeBlock: false, horizontalRule: false,
      }),
      Underline,
      TextStyle,
      Color,
    ],
    content: value,
    editorProps: {
      attributes: {
        "data-testid": testId,
        class: "prose prose-sm max-w-none focus:outline-none min-h-[38px] px-3 py-2 text-xs",
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value && value != null) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const insertToken = (tok) => editor?.chain().focus().insertContent(`&lt;${tok}&gt;`).run();
  const pickColor = () => {
    const current = editor?.getAttributes("textStyle")?.color || "#4b5563";
    const c = window.prompt("Text colour (hex)", current);
    if (c && /^#?[0-9a-fA-F]{3,8}$/.test(c)) {
      editor?.chain().focus().setColor(c.startsWith("#") ? c : `#${c}`).run();
    }
  };

  const btn = (Icon, label, active, onClick, tid) => (
    <button
      type="button"
      title={label}
      onClick={onClick}
      data-testid={tid}
      className={`w-7 h-7 rounded flex items-center justify-center transition text-[11px] ${active ? "bg-[var(--surface-strong)] text-[var(--accent)]" : "text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"}`}
    >
      <Icon size={12} weight={active ? "fill" : "regular"} />
    </button>
  );

  if (!editor) return <div className="h-[62px] rounded-md border border-[var(--border)] bg-[var(--surface)]" />;

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-[var(--border)] bg-[var(--surface-2)]/60">
        {btn(TextB, "Bold", editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), `${testId}-bold`)}
        {btn(TextItalic, "Italic", editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), `${testId}-italic`)}
        {btn(TextUnderline, "Underline", editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), `${testId}-underline`)}
        {btn(PaintBucket, "Colour", false, pickColor, `${testId}-color`)}
        <span className="mx-1 h-4 w-px bg-[var(--border)]" />
        {tokens.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => insertToken(t)}
            data-testid={`${testId}-token-${t}`}
            className="px-1.5 h-6 rounded text-[10px] font-mono text-[var(--text-3)] hover:text-[var(--accent)] hover:bg-[var(--surface-2)]"
            title={`Insert <${t}>`}
          >
            &lt;{t}&gt;
          </button>
        ))}
      </div>
      <EditorContent editor={editor} />
      {placeholder && !editor.getText() && (
        <div className="-mt-[34px] ps-3 text-[10px] text-[var(--text-3)] pointer-events-none select-none">{placeholder}</div>
      )}
    </div>
  );
}
